import React from 'react';
import cx from 'classnames';
import { isObjectEmpty } from '@common/paymentsUtils/inputValidator';
import { Heading, Page, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftOutline } from '@zillow/constellation-icons';
import { ErrorPage, ErrorPageContainer } from '@zg-rentals/visual-cohesion';
import { propertyUtils } from '@zg-rentals/rental-manager-env';
import Skeleton from '@rental-manager/components/Skeleton';
import './index.scss';
import type { IClusterSettingsModel, ICookieEnabledServiceModel, ILocationModel } from '@rental-manager/models/Types';
import type { RouteComponentProps } from 'react-router-dom';

type Props = {
  propertyId: string;
  encodedLeaseId: string | Array<string> | null;
  getIreneDashboard: (...args: any) => void;
  location: ILocationModel;
  getListing: (propertyId: string) => any;
  signUrl: (...args: any) => void;
  clusterSettings: IClusterSettingsModel;
  isInWebView: boolean;
  cookieEnabledServices: ICookieEnabledServiceModel;
  children: (...args: Array<any>) => any;
  fromAggregateView: boolean;
};

type State = {
  isLoading: boolean;
  listing: { [key: string]: any };
};

class PropertyDetailsPage extends React.Component<Props & RouteComponentProps, State> {
  brand = 'zillow';

  state = {
    isLoading: true,
    listing: {
      propertyPhotos: [],
      photos: [],
    },
  };

  componentDidMount() {
    const { propertyId, encodedLeaseId } = this.props;
    const onSettled = () => this.props.getIreneDashboard(propertyId, encodedLeaseId);
    this.getListing().then(onSettled, onSettled);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !nextState.isLoading;
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.getListing();
    }
  }

  getListing = () => {
    const { propertyId } = this.props;
    return this.props.getListing(propertyId).then((response) => {
      this.setState({
        isLoading: false,
        listing: response.error ? {} : response,
      });
    });
  };

  signUrl = async (url, brand) => {
    const params = { brandName: brand };
    return this.props.signUrl(url, params);
  };

  renderPDP = () => {
    const { listing } = this.state;
    const { clusterSettings, children, isInWebView, cookieEnabledServices, fromAggregateView } = this.props;
    // FIXME: RMW remapped the default response name of photos but PDPHeader expects propertyPhotos
    listing.propertyPhotos = listing.photos;

    const { hostApp } = cookieEnabledServices;
    const isInMobileWebView = hostApp === 'ios' || hostApp === 'android';

    return (
      <div
        className={cx({
          'PDP-page-native': isInWebView,
        })}
      >
        <Page.Root className="PDPConnected" data-testid="PDPConnected" css={{ backgroundColor: 'transparent' }}>
          {!isInMobileWebView && (
            <Page.Breadcrumb data-testid="PageBreadcrumb">
              <TextButton icon={<IconChevronLeftOutline />} asChild>
                <a
                  href={
                    fromAggregateView
                      ? '/rental-manager/rent-payment/overview'
                      : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        `/rental-manager/properties/${listing.listingAlias}/dashboard/overview`
                  }
                >
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-expect-error */}
                  {fromAggregateView ? 'Back' : propertyUtils.formatAddress(listing).formattedCompleteAddress}
                </a>
              </TextButton>
            </Page.Breadcrumb>
          )}
          <Page.Header>
            <Heading level={1}>Payments</Heading>
          </Page.Header>
          <Page.Content>{children({ listing, clusterSettings })}</Page.Content>
        </Page.Root>
      </div>
    );
  };

  renderListingNotFound = () => (
    <ErrorPageContainer layout="header-nav">
      <ErrorPage
        errorMessage="We can’t find that page"
        errorDetails="That page may have moved or no longer exists. Let’s get you back on track."
        action={<a href="/rental-manager/">Go to Rental Manager</a>}
      />
    </ErrorPageContainer>
  );

  renderSkeleton = () => <Skeleton />;

  render() {
    const { isLoading, listing } = this.state;
    if (isLoading) {
      return this.renderSkeleton();
    }

    const hasListing = !isObjectEmpty(listing);
    if (hasListing) {
      return this.renderPDP();
    } else {
      return this.renderListingNotFound();
    }
  }
}

export default PropertyDetailsPage;
